import cx from 'clsx';
import React from 'react';
import { type Control, useController } from 'react-hook-form';

import { ReactComponent as CloseIcon } from '@assets/svg/cross.svg';
import { ReactComponent as SearchIcon } from '@assets/svg/search.svg';

import useTranslation from '@hooks/useTranslation';

import style from './style.module.scss';

interface ICommonProps {
  placeholder?: string;
  dataQa?: string;
  variant?: 'header-search' | 'table-search';
  className?: string;
  afterReset?: () => void;
}

interface IUncontrolledProps extends ICommonProps {
  value?: string;
  onChange: (value: string) => void;
}

interface IControlledProps extends ICommonProps {
  name: string;
  control: Control<any>;
}

const Component: React.FC<IUncontrolledProps> = ({
  placeholder,
  dataQa = 'search',
  value,
  variant,
  className,
  onChange,
  afterReset,
}) => {
  const { t } = useTranslation();

  const handleReset = () => {
    onChange('');
    afterReset && afterReset();
  };

  const isHeaderInputSearch = variant === 'header-search';
  const isTableInputSearch = variant === 'table-search';

  return (
    <div
      className={cx(
        style.inputSearchWrapper,
        {
          [style.headerInputSearchWrapper]: isHeaderInputSearch,
          [style.tableInputSearchWrapper]: isTableInputSearch,
        },
        className,
      )}
    >
      <div className={style.searchIconBlock}>
        <SearchIcon className={style.searchIcon} />
      </div>
      <input
        type="text"
        className={cx(style.inputSearch, {
          [style.active]: value,
          [style.headerInputSearch]: isHeaderInputSearch,
          [style.tableInputSearch]: isTableInputSearch,
        })}
        placeholder={placeholder || `${t('Search...')}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={250}
        data-qa={dataQa}
      />
      {value && (
        <div className={style.searchResetBlock}>
          <CloseIcon className={style.searchResetIcon} onClick={handleReset} />
        </div>
      )}
    </div>
  );
};

const Controlled: React.FC<IControlledProps> = ({
  placeholder,
  name,
  control,
  variant,
  dataQa,
  className,
  afterReset,
}) => {
  const {
    field: { value, onChange },
  } = useController({
    control,
    name,
  });

  return (
    <Component
      placeholder={placeholder}
      dataQa={dataQa}
      value={value}
      variant={variant}
      className={className}
      onChange={onChange}
      afterReset={afterReset}
    />
  );
};

const FilterSearch: React.FC<IControlledProps | IUncontrolledProps> = (props) => {
  if ('control' in props) {
    return <Controlled {...props} />;
  }
  return <Component {...props} />;
};

export default FilterSearch;
